import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  DruckverlustStrang, DruckverlustTeilstrecke, LueftungGrundlagen, SchallAnlage, SchallLauf, SchallUebersicht,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState, StatusBadge,
  Vorbehalte, formatiereZahl, spalteFuer, type Spalte,
} from '../../muster';
import { rechenstandText } from './pruefung';
import { RaumakustikAbschnitt } from './SchallAnnahmen';
import { Berichtsvorbehaltkasten, useBerichtsvorbehalte } from './LueftungBerichtsvorbehalte';
import {
  ZIEL_RUECKFALL, massgeblichText, spektrum, wegVorbehalte, ziel,
} from './schallregeln';
import './schall.css';

/** Eine Zeile eines Spektrums: das Oktavband und je Reihe ein Pegel. */
type Bandzeile = ReturnType<typeof spektrum>[number];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SCHALLBERECHNUNG JE ANLAGE UND WEG (TV6b, TX-STANDARD 1.6 S1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eigene Seite und nicht eine Spalte in der Druckverlustrechnung, weil sie
 * eine andere Frage beantwortet: Der Druckverlust sagt, was der Ventilator
 * leisten muss - der Schall, ob der Raum bewohnbar ist. Beide hängen am selben
 * Strang, aber die maßgebende Stelle ist selten dieselbe.
 *
 * Seit TV6b über die Muster: Kopf mit Rechenstand und Exportleiste (PDF
 * LUE-SCH), Vorbehalte (Annahmensatz, LUE-39, Wege ohne Kennwerte), Wege als
 * `DataTable` mit Zeilenwahl statt einer Knopfliste, Spektrum als `DataTable`
 * statt roher Tabelle, Hinweise des Laufs ohne Kappung (vorher 12), Zahlen über
 * `formatiereZahl`, Abschnitt „Annahmen und Vorgaben“ mit der Raumakustik.
 */
export function Schall() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [uebersicht, setUebersicht] = useState<SchallUebersicht | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [stand, setStand] = useState<string | null>(null);
  const [grundlagen, setGrundlagen] = useState<LueftungGrundlagen | null>(null);
  const [grundlagenFehler, setGrundlagenFehler] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<SchallAnlage | null>(null);
  const [rechnet, setRechnet] = useState(false);
  const [rechenfehler, setRechenfehler] = useState<unknown>(null);
  const [lauf, setLauf] = useState<SchallLauf | null>(null);
  // Zählt die Läufe dieser Sitzung: Nach einem Lauf lädt der gewählte Weg neu.
  const [laeufe, setLaeufe] = useState(0);
  // TV7a-2 (§22 c): der Vorbehaltskasten des Berichts LUE-SCH, Satz für Satz wie im PDF.
  const berichtsvorbehalte = useBerichtsvorbehalte(api, projekt.id, 'schall', {}, laeufe);

  const laden = useCallback(() => {
    setFehler(null);
    api.schallUebersicht(projekt.id).then(setUebersicht).catch(setFehler);
  }, [api, projekt.id]);

  // Rechenstand und Grundlagen mit eigenem catch: Ohne sie fehlen nur Kopfzeile und Annahmen.
  const nebenbeiLaden = useCallback(() => {
    api.druckverlustUebersicht(projekt.id).then((d) => setStand(d.stand)).catch(() => setStand(null));
    setGrundlagenFehler(false);
    api.lueftungGrundlagen(projekt.id).then(setGrundlagen).catch(() => { setGrundlagen(null); setGrundlagenFehler(true); });
  }, [api, projekt.id]);

  useEffect(() => { laden(); nebenbeiLaden(); }, [laden, nebenbeiLaden]);

  /**
   * Die Berechnung auslösen. Es ist derselbe Lauf wie beim Druckverlust - die
   * Schallkette hängt an denselben Teilstrecken; die Meldung danach sagt das.
   */
  const rechnen = async () => {
    setRechnet(true);
    setRechenfehler(null);
    try {
      const e = await api.schallRechnen(projekt.id);
      setLauf(e);
      setUebersicht(e.uebersicht);
      setLaeufe((x) => x + 1);
      nebenbeiLaden();
    } catch (e) {
      setRechenfehler(e);
    } finally {
      setRechnet(false);
    }
  };

  const u = uebersicht;
  const ohneLauf = !!u && (u.anlagen ?? []).length === 0;
  const ueberZiel = (u?.anlagen ?? []).reduce((s, a) => s + a.ueberZiel, 0);

  const spalten = useMemo<Spalte<SchallAnlage>[]>(() => [
    { schluessel: 'anlage', titel: 'Anlage', wert: (a) => a.anlage, immerSichtbar: true },
    { schluessel: 'weg', titel: 'Weg', wert: (a) => a.weg },
    { schluessel: 'auslaesse', titel: 'Auslässe', art: 'zahl', nachkomma: 0, wert: (a) => a.auslaesse },
    {
      schluessel: 'mitSchall', titel: 'Stränge mit Ergebnis', art: 'zahl', nachkomma: 0, wert: (a) => a.mitSchall,
      inhalt: (a) => `${formatiereZahl(a.mitSchall, 0)} von ${formatiereZahl(a.straenge, 0)}`,
    },
    { schluessel: 'lpa', titel: 'L_pA direkt max.', ...spalteFuer('schallpegel_dba'), wert: (a) => a.lpaMax },
    { schluessel: 'lwa', titel: 'L_WA Auslass max.', ...spalteFuer('schallpegel_dba'), wert: (a) => a.lwaMax },
    { schluessel: 'daempfung', titel: 'Dämpfung', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (a) => a.daempfung },
    { schluessel: 'massgeblich', titel: 'maßgeblich', wert: (a) => a.massgeblich ?? '' },
    {
      schluessel: 'ueberZiel', titel: 'über Zielpegel', art: 'zahl', nachkomma: 0, wert: (a) => a.ueberZiel,
      inhalt: (a) => (a.ueberZiel > 0
        ? <StatusBadge zustand="warnung" text={`${formatiereZahl(a.ueberZiel, 0)} über Ziel`} klein />
        : '—'),
    },
    {
      schluessel: 'kennwerte', titel: 'Kennwerte', art: 'status',
      wert: (a) => (a.ohneKennwerte ? 'untere Schranke' : 'vollständig'),
      inhalt: (a) => (a.ohneKennwerte
        ? <StatusBadge zustand="warnung" text="untere Schranke" klein />
        : <StatusBadge zustand="neutral" text="vollständig" klein />),
    },
  ], []);

  return (
    <>
      <PageHeader
        titel="Schallberechnung Lüftung"
        beschreibung="Die Kette vom Geräteanschluss bis in den Raum je Oktavband: Aufteilung an jedem Abzweig, Dämpfung in Kanal, Bogen und Kulisse, Mündungsreflexion, dazu das Eigengeräusch der Bauteile."
        meta={(
          <span data-rechenstand>
            {rechenstandText(stand
              ? { lauf: 'Kanalnetzlauf', werkzeug: 'druckverlust-lueftung', gerechnetAm: stand, modellstand: null, datenstand: '' }
              : null)}
          </span>
        )}
        aktionen={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt" onClick={() => void rechnen()} disabled={rechnet || !u}>
              {rechnet ? 'rechnet …' : 'Schallberechnung neu rechnen'}
            </button>
            <ExportLeiste ziele={[{
              art: 'pdf', beschriftung: 'PDF LUE-SCH', laden: () => api.lueftungSchallberichtPdf(projekt.id),
              gesperrt: ohneLauf || !u ? { grund: 'Noch keine Schallberechnung.' } : undefined,
            }]}
            />
          </>
        )}
      />

      <p className="lue-schall-meldung" role="status" data-meldung="lauf">
        {lauf
          ? `${formatiereZahl(lauf.anzahlAnlagen, 0)} Anlagen gerechnet - ${formatiereZahl(lauf.netze, 0)} Netze, davon `
            + `${formatiereZahl(lauf.ringnetze, 0)} Ringnetze. Derselbe Lauf hat auch den Druckverlust neu gerechnet.`
          : ''}
      </p>

      {rechenfehler !== null && (
        <ErrorState klein meldung="Die Schallberechnung ist gescheitert." technisch={rechenfehler}
                    beiErneutVersuchen={() => void rechnen()} />
      )}

      {!u && fehler === null && <LoadingState text="Schallübersicht wird abgerufen …" zeilen={8} spalten={4} />}
      {fehler !== null && (
        <ErrorState meldung="Die Schallübersicht konnte nicht abgerufen werden." technisch={fehler}
                    beiErneutVersuchen={laden} />
      )}

      {ohneLauf && (
        <EmptyState
          titel="Für dieses Modell wurde noch keine Schallberechnung durchgeführt."
          erklaerung="Die Schallkette entsteht aus denselben Teilstrecken wie die Druckverlustrechnung. Ohne aufgenommenes Kanalnetz gibt es keinen Weg vom Geräteanschluss in den Raum. „Schallberechnung neu rechnen“ rechnet beides."
        />
      )}

      {u && !ohneLauf && (
        <>
          <Berichtsvorbehaltkasten stand={berichtsvorbehalte} bericht="schall" />

          <KpiReihe>
            <KpiCard titel="Wege" wert={u.anzahlWege} />
            <KpiCard titel="mit Schallergebnis" wert={u.mitSchall} vergleich={`von ${formatiereZahl(u.anzahlWege, 0)} Wegen`} />
            <KpiCard
              titel="Stränge über Zielpegel" wert={ueberZiel}
              zustand={ueberZiel > 0 ? 'warnung' : 'gut'}
              zustandText={ueberZiel > 0 ? 'über Ziel' : 'kein Strang über Ziel'}
              vergleich={`auf ${formatiereZahl(u.ueberZielWege, 0)} Wegen`}
            />
            <KpiCard titel="Wege ohne Kennwerte" wert={u.ohneKennwerte}
                     vergleich={u.ohneKennwerte > 0 ? 'Pegel dort untere Schranke' : undefined} />
          </KpiReihe>

          {lauf && lauf.hinweise.length > 0 && (
            <ContentSection titel="Hinweise des Laufs" aufklappbar standardOffen={false}>
              <DataTable<{ nummer: number; text: string }>
                name="Hinweise des Laufs"
                spalten={[
                  { schluessel: 'nummer', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (h) => h.nummer },
                  { schluessel: 'text', titel: 'Hinweis', wert: (h) => h.text },
                ]}
                zeilen={lauf.hinweise.map((text, i) => ({ nummer: i + 1, text }))}
                schluessel={(h) => String(h.nummer)}
                dicht
              />
            </ContentSection>
          )}

          <ContentSection
            titel="Wege"
            beschreibung="Zuluft- und Abluftweg stehen getrennt - sie hängen an verschiedenen Ventilatoren und Geräteanschlüssen. Eine Zeile wählen zeigt den Weg mit allen Strängen."
          >
            <DataTable<SchallAnlage>
              name="Wege je Anlage"
              spalten={spalten}
              zeilen={u.anlagen}
              schluessel={(a) => `${a.anlage}|${a.weg}`}
              sortierung={{ spalte: 'lpa', richtung: 'ab' }}
              beiZeileKlick={(a) => setGewaehlt(a)}
              aktiveZeile={gewaehlt ? `${gewaehlt.anlage}|${gewaehlt.weg}` : undefined}
              suchePlatzhalter="Anlage oder Weg"
              ausfuhr
              ausfuhrDateiname="schall-wege.csv"
              dicht
            />
          </ContentSection>

          {gewaehlt && (
            <Wegblatt key={`${gewaehlt.anlage}|${gewaehlt.weg}|${laeufe}`} projektId={projekt.id} anlage={gewaehlt} />
          )}

          <ContentSection titel="Was hier steht - und was nicht">
            <p className="lue-schall-text">
              „L_pA direkt“ ist der Direktschallanteil eines einzelnen Auslasses, 1,5 m unter ihm im Halbraum. Er ist
              nicht der Raumpegel: Ein Raum hat selten einen Auslass, und das Diffusfeld kommt dazu. Den Raumpegel aus
              der energetischen Summe aller Auslässe eines Raums und der Absorptionsfläche nach Sabine zeigt die
              Seite <Link to={`/projekte/${projekt.id}/lueftung/raumschall`}>Raumschall</Link>. Diese Seite beantwortet
              dafür die Frage, welcher Auslass den Raum laut macht.
            </p>
            <p className="lue-schall-text">
              Die Kanaldämpfung ist die des blanken Kanals nach VDI 2081 Blatt 1 (rund, eckig und flexibel je eigener
              Tabelle); eine Innendämmung wird nicht angesetzt, weil sie im Modell nicht steht. A-bewertet wird erst
              am Ende, weil jede Dämpfung frequenzabhängig ist. Die Schallberechnung ist derselbe Lauf wie der
              Druckverlust: Wer hier rechnet, hat danach auch einen neuen Druckverlust.
            </p>
          </ContentSection>

          <RaumakustikAbschnitt
            daten={grundlagen?.raumakustik ?? (grundlagenFehler ? [] : null)}
            fehler={grundlagenFehler}
            zusatz={(
              <p className="lue-schall-text">
                Trägt der Raum eines Strangs keine Nutzungsart, steht der Strang gegen {formatiereZahl(ZIEL_RUECKFALL, 0)} dB(A),
                den strengsten Zielpegel der Tabelle - die sichere Richtung.
              </p>
            )}
          />
        </>
      )}
    </>
  );
}

// ═══ Ein Weg ════════════════════════════════════════════════════════════════

function Wegblatt({ projektId, anlage }: { projektId: string; anlage: SchallAnlage }) {
  const { api } = useOutletContext<Rahmen>();
  const [strang, setStrang] = useState<DruckverlustStrang | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(() => {
    setFehler(null);
    api.druckverlustAnlage(projektId, anlage.anlage, anlage.weg).then(setStrang).catch(setFehler);
  }, [api, projektId, anlage.anlage, anlage.weg]);

  useEffect(() => { laden(); }, [laden]);

  const spalten = useMemo<Spalte<DruckverlustTeilstrecke>[]>(() => [
    { schluessel: 'nummer', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (t) => t.nummer },
    { schluessel: 'raum', titel: 'Raum', immerSichtbar: true, wert: (t) => t.raumnummer ?? '' },
    { schluessel: 'ebene', titel: 'Ebene', wert: (t) => t.ebene ?? '' },
    { schluessel: 'volumenstrom', titel: 'Volumenstrom', ...spalteFuer('volumenstrom_luft'), wert: (t) => t.volumenstromM3h },
    { schluessel: 'druckverlust', titel: 'Druckverlust', art: 'zahl', einheit: 'Pa', nachkomma: 0, wert: (t) => t.druckverlustPa },
    { schluessel: 'lwa', titel: 'L_WA Auslass', ...spalteFuer('schallpegel_dba'), wert: (t) => t.lwaAuslassDb },
    { schluessel: 'daempfung', titel: 'Dämpfung', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (t) => t.daempfungDb },
    {
      schluessel: 'lpa', titel: 'L_pA direkt', ...spalteFuer('schallpegel_dba'), wert: (t) => t.lpaDirektDb,
      inhalt: (t) => ((t.lpaDirektDb ?? 0) > ziel(t)
        ? <StatusBadge zustand="warnung" text={`${formatiereZahl(t.lpaDirektDb, 1)} über Ziel`} klein />
        : formatiereZahl(t.lpaDirektDb, 1)),
    },
    {
      schluessel: 'ziel', titel: 'Zielpegel', art: 'zahl', einheit: 'dB(A)', nachkomma: 0, wert: (t) => ziel(t),
      inhalt: (t) => (t.zielpegelDbA === null
        ? <>{formatiereZahl(ZIEL_RUECKFALL, 0)} <StatusBadge zustand="neutral" text="Rückfall" klein /></>
        : formatiereZahl(t.zielpegelDbA, 0)),
    },
    { schluessel: 'massgeblich', titel: 'maßgeblich', wert: (t) => t.massgeblich ?? '' },
  ], []);

  const titel = `${anlage.anlage} · ${anlage.weg}`;
  const aktionen = (
    <ExportLeiste ziele={[{
      art: 'pdf', beschriftung: 'Schallnachweis dieser Anlage', laden: () => api.lueftungSchallberichtPdf(projektId, anlage.anlage),
    }]}
    />
  );

  if (fehler !== null) {
    return (
      <ContentSection titel={titel}>
        <ErrorState meldung="Der Weg konnte nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={laden} />
      </ContentSection>
    );
  }
  if (!strang) {
    return (
      <ContentSection titel={titel}>
        <LoadingState text="Weg wird abgerufen …" zeilen={6} spalten={5} />
      </ContentSection>
    );
  }

  const mitSchall = (strang.teilstrecken ?? [])
    .filter((t) => t.lpaDirektDb !== null)
    .sort((a, b) => (b.lpaDirektDb ?? 0) - (a.lpaDirektDb ?? 0));

  if (mitSchall.length === 0) {
    return (
      <ContentSection titel={titel} aktionen={aktionen}>
        <EmptyState
          titel="Für diesen Weg liegt kein Schallergebnis vor."
          erklaerung="Die Kette braucht den Anschlusspegel des Geräts - beim Zuluftweg den Zuluftstutzen, beim Abluftweg den Abluftstutzen. Fehlt er, wird nichts geschätzt: Die Geräte haben integrierte Schalldämpfer, und ein Ventilatorpegel ohne sie läge zwanzig Dezibel zu hoch."
        />
      </ContentSection>
    );
  }

  const lautester = mitSchall[0];
  const ueber = (lautester.lpaDirektDb ?? 0) > ziel(lautester);

  return (
    <ContentSection
      titel={titel}
      beschreibung={`${formatiereZahl(mitSchall.length, 0)} Stränge mit Schallergebnis, nach Direktschallanteil geordnet.`}
      aktionen={aktionen}
    >
      <div className="lue-schall-detail">
        <Vorbehalte eintraege={wegVorbehalte(anlage, strang.teilstrecken ?? [])} />

        <KpiReihe>
          <KpiCard
            titel="lautester Auslass, L_pA direkt" wert={lautester.lpaDirektDb} einheit="dB(A)" nachkomma={1}
            zustand={ueber ? 'warnung' : 'gut'}
            zustandText={`${ueber ? 'über' : 'unter'} Zielpegel ${formatiereZahl(ziel(lautester), 0)} dB(A)`}
            vergleich={lautester.raumnummer ? `Raum ${lautester.raumnummer}` : undefined}
          />
          <KpiCard titel="L_WA am Auslass" wert={lautester.lwaAuslassDb} einheit="dB(A)" nachkomma={1} />
          <KpiCard titel="Dämpfung im Weg" wert={lautester.daempfungDb} einheit="dB" nachkomma={1} />
          <KpiCard titel="maßgeblich" wert={lautester.massgeblich ?? '—'} />
        </KpiReihe>

        <p className="lue-schall-text">{massgeblichText(lautester.massgeblich)}</p>

        {lautester.lwOktaven && (
          <DataTable<Bandzeile>
            name="Spektrum am lautesten Auslass"
            spalten={[
              { schluessel: 'band', titel: 'Oktavband', art: 'zahl', einheit: 'Hz', nachkomma: 0, wert: (z) => z.band },
              { schluessel: 'lw', titel: 'L_W', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (z) => z.lw },
            ]}
            zeilen={spektrum({ lw: lautester.lwOktaven })}
            schluessel={(z) => String(z.band)}
            suche={false}
            dicht
          />
        )}

        <DataTable<DruckverlustTeilstrecke>
          name="Alle Stränge"
          beschreibung="Gekennzeichnet ist, was den Zielpegel seiner Nutzungsart überschreitet."
          spalten={spalten}
          zeilen={mitSchall}
          schluessel={(t) => String(t.nummer)}
          sortierung={{ spalte: 'lpa', richtung: 'ab' }}
          ausfuhr
          ausfuhrDateiname={`schall-${anlage.anlage}-${anlage.weg}.csv`}
          dicht
        />
      </div>
    </ContentSection>
  );
}
